import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { LOGO_SRC } from "../../data/menu";
import { APP } from "../../constants/testIds";
import { scrollToId } from "../../hooks/useLenisScroll";

const words = ["Cabalgamos", "por", "sabores", "que", "no", "se", "olvidan."];

export const Hero = () => {
  const ref = useRef(null);
  const prefersReduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, prefersReduced ? 0 : -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.3]);

  return (
    <section
      ref={ref}
      data-testid={APP.hero}
      id="inicio"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0a0906] text-[#f4e9d5]"
    >
      {/* Subtle wood/grain layers */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] noise-texture" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_-10%,rgba(201,169,97,0.18),transparent_55%)]" />
      <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-t from-[#0a0906] to-transparent" />

      {/* Top meta bar */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-5 pt-6 sm:px-8 sm:pt-8">
        <span className="font-inter text-[10px] uppercase tracking-[0.28em] text-[#c9a961]/80">
          Est. Restaurante
        </span>
        <span className="font-inter text-[10px] uppercase tracking-[0.28em] text-[#f4e9d5]/60">
          México · MX
        </span>
      </div>

      {/* Center composition */}
      <motion.div
        style={{ y: yLogo, opacity }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center px-5 pt-10 sm:px-8 sm:pt-16"
      >
        <motion.img
          src={LOGO_SRC}
          data-testid={APP.heroLogo}
          alt="Los Andariegos Restaurante"
          draggable={false}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="w-[78%] sm:w-[52%] md:w-[44%] lg:w-[36%] max-w-[520px] select-none drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)]"
        />

        {/* Rule */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1], delay: 0.6 }}
          className="mt-8 h-px w-24 origin-center bg-[#c9a961]/60 sm:w-32"
        />

        {/* Carta 2026 */}
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 text-center font-rye text-3xl leading-none text-[#f4e9d5] sm:text-5xl md:text-6xl"
        >
          Carta <span className="text-[#c9a961]">2026</span>
        </motion.h1>

        {/* Masked line-by-line reveal */}
        <div className="mt-8 flex flex-wrap justify-center gap-x-[0.4em] gap-y-1 px-4 text-center font-playfair text-xl italic text-[#f4e9d5]/90 sm:text-2xl md:text-3xl">
          {words.map((w, i) => (
            <span key={i} className="overflow-hidden inline-block leading-[1.2]">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.75,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.9 + i * 0.06,
                }}
                className="inline-block"
              >
                {w}
              </motion.span>
            </span>
          ))}
        </div>

        {/* Chapter mark */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="mt-14 flex items-center gap-4 font-inter text-[10px] uppercase tracking-[0.32em] text-[#f4e9d5]/50"
        >
          <span className="h-px w-8 bg-[#c9a961]/50" />
          Carta digital · Sitio Oficial
          <span className="h-px w-8 bg-[#c9a961]/50" />
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        data-testid={APP.heroCta}
        onClick={() => scrollToId("entradas", -80)}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2 group"
        aria-label="Explorar el menú"
      >
        <span className="font-inter text-[10px] uppercase tracking-[0.28em] text-[#c9a961] group-hover:text-[#f4e9d5] transition-colors">
          Explora la carta
        </span>
        <motion.div
          animate={prefersReduced ? {} : { y: [0, 6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-[#c9a961] to-transparent"
        />
      </motion.button>
    </section>
  );
};
